import { COMMISSIONER_ONLY_NOTICE } from './constants';
import {
  isCoreSetupComplete,
  isOperationalReadinessKey,
  sanitizeCustomerReadinessCopy,
} from './leagueReadinessMapper';
import { lscPath } from './routing';
import {
  SCHEDULE_SYNC_ROLLOVER_DETECTED_COPY,
  SCHEDULE_SYNC_ROLLOVER_REVIEW_COPY,
  SCHEDULE_SYNC_TEAM_ASSIGNMENT_REVIEW_COPY,
  guardedAutoSyncApplyCustomerCopy,
} from './setupScheduleCopy';
import {
  SCHEDULE_REPAIR_DETECTED_COPY,
  SCHEDULE_REPAIR_PREVIEW_CTA,
} from './scheduleRepairCopy';
import type {
  LscAutomationActivationPayload,
  LscAutomationActivationStatus,
  LscBillingStatusResponse,
  LscCommissionerDigest,
  LscLeagueReadiness,
  LscLeagueReadinessItem,
  LscSyncStatus,
  LscVodHandoffSession,
} from './types';
import {
  classifyVodHandoffSession,
  formatVodHandoffSessionMatchLabel,
  getVodHandoffSessionPrimaryText,
  selectPanelHandoffSessions,
} from './vodHandoffSessionTruth';
import { buildInternalCopyPattern } from './internalSanitizerPatterns';

export type CommandCenterBlockerCategory =
  | 'launch'
  | 'automation'
  | 'review'
  | 'waiting'
  | 'billing_access'
  | 'healthy';

export type CommandCenterBlockerSeverity =
  | 'blocking'
  | 'action_required'
  | 'waiting'
  | 'warning'
  | 'healthy'
  | 'optional';

export interface CommandCenterBlocker {
  id: string;
  category: CommandCenterBlockerCategory;
  severity: CommandCenterBlockerSeverity;
  title: string;
  description: string;
  primaryActionLabel?: string;
  primaryActionHref?: string;
  source: string;
  customerSafeReason: string;
  blocksLaunch: boolean;
  blocksAutomation: boolean;
  requiresCommissioner: boolean;
  isSystemWaiting: boolean;
  isResolved: boolean;
  priority: number;
}

export interface CommandCenterBlockerContext {
  leagueId: string;
  readiness: LscLeagueReadiness | null;
  activation: LscAutomationActivationPayload | null;
  billingStatus: LscBillingStatusResponse | null;
  syncStatus: LscSyncStatus | null;
  digest: LscCommissionerDigest | null;
  handoffSessions: LscVodHandoffSession[];
  authError?: 'unauthorized' | 'forbidden' | null;
  isOwner: boolean;
  isOperator: boolean;
}

export const COMMAND_CENTER_BLOCKER_CATEGORY_LABELS: Record<CommandCenterBlockerCategory, string> = {
  launch: 'Launch blockers',
  automation: 'Automation blockers',
  review: 'Review needed',
  waiting: 'Waiting on system',
  billing_access: 'Billing and access',
  healthy: 'Completed / healthy',
};

const PRIORITY = {
  UNAUTHORIZED: 10,
  FORBIDDEN: 20,
  BILLING_INACTIVE: 100,
  BILLING_UNBOUND: 110,
  BILLING_OWNER_MANAGED: 120,
  SETUP_INCOMPLETE: 200,
  SCHEDULE_MISSING: 300,
  SCHEDULE_STALE: 310,
  SEASON_ROLLOVER: 320,
  TEAM_ASSIGNMENT: 400,
  MEMBERS_MISSING: 410,
  TWITCH_MISSING: 420,
  AUTOMATION_NOT_ACTIVATED: 500,
  AUTOMATION_RUNTIME_BLOCKED: 510,
  SIDE_CONFIRMATION: 600,
  NEEDS_ATTENTION: 650,
  ARCHIVE_CANDIDATE_READY: 700,
  VOD_WAITING: 800,
  VOD_RETRY: 810,
  VOD_RETRY_EXHAUSTED: 820,
  HEALTHY_SETUP: 9000,
  HEALTHY_AUTOMATION: 9010,
  HEALTHY_AUTOPUBLISH_OFF: 9020,
} as const;

const READINESS_LAUNCH_KEYS = new Set([
  'league_profile',
  'billing_entitlement',
  'schedule_source_connected',
  'current_week_synced',
  'active_games',
  'games_visible',
  'members_imported',
  'team_assignments',
  'twitch_linked',
  'eventsub_ready',
  'live_detection_active',
]);

const ACTIVATION_SCHEDULE_BLOCKERS = new Set([
  'schedule_source_connected',
  'current_week_synced',
  'active_games',
  'games_visible',
]);

const INTERNAL_COPY_PATTERN = buildInternalCopyPattern(
  'firestore|entitlement_id|operator|session_id|candidate_id|env_|__ENV_PREFIX__|stripe_customer|subscription_id'
);

function leagueHref(leagueId: string, suffix: string): string {
  return lscPath(`/leagues/${leagueId}${suffix}`);
}

function permissionAwareAction(
  item: Pick<LscLeagueReadinessItem, 'action_label' | 'action_href' | 'admin_only'>,
  isOperator: boolean
): { label?: string; href?: string; requiresCommissioner: boolean } {
  if (item.admin_only && !isOperator) {
    return { requiresCommissioner: true };
  }
  return {
    label: item.action_label,
    href: item.action_href,
    requiresCommissioner: false,
  };
}

function blockerFromReadinessItem(
  item: LscLeagueReadinessItem,
  context: CommandCenterBlockerContext,
  config: {
    id: string;
    category: CommandCenterBlockerCategory;
    priority: number;
    blocksLaunch: boolean;
    blocksAutomation: boolean;
  }
): CommandCenterBlocker | null {
  if (item.status === 'complete' || item.status === 'not_applicable') {
    return null;
  }
  if (isOperationalReadinessKey(item.key)) {
    return null;
  }

  const action = permissionAwareAction(item, context.isOperator);
  const title = sanitizeCustomerReadinessCopy(item.title, item.key);
  const description = action.requiresCommissioner
    ? COMMISSIONER_ONLY_NOTICE
    : sanitizeCustomerReadinessCopy(item.description, item.key);

  const severity: CommandCenterBlockerSeverity =
    item.status === 'blocked' && item.severity === 'blocking'
      ? 'blocking'
      : item.status === 'blocked'
        ? 'action_required'
        : 'warning';

  return {
    id: config.id,
    category: config.category,
    severity,
    title,
    description,
    primaryActionLabel: action.requiresCommissioner ? 'Ask a commissioner' : action.label,
    primaryActionHref: action.requiresCommissioner ? undefined : action.href,
    source: item.source ?? 'league_readiness',
    customerSafeReason: description,
    blocksLaunch: config.blocksLaunch,
    blocksAutomation: config.blocksAutomation,
    requiresCommissioner: action.requiresCommissioner,
    isSystemWaiting: false,
    isResolved: false,
    priority: config.priority,
  };
}

function readinessPriorityForKey(key: string, context?: CommandCenterBlockerContext): number {
  switch (key) {
    case 'billing_entitlement': {
      const lifecycleMode = (context?.billingStatus?.billing_lifecycle?.mode || '').toLowerCase();
      const subscriptionState = (context?.billingStatus?.subscription_state || '').toLowerCase();
      if (
        lifecycleMode === 'billing_unbound' ||
        subscriptionState === 'account_active_unbound' ||
        context?.billingStatus?.status === 'unbound'
      ) {
        return PRIORITY.BILLING_UNBOUND;
      }
      return PRIORITY.BILLING_INACTIVE;
    }
    case 'league_profile':
      return PRIORITY.SETUP_INCOMPLETE;
    case 'schedule_source_connected':
    case 'active_games':
    case 'games_visible':
      return PRIORITY.SCHEDULE_MISSING;
    case 'current_week_synced':
      return PRIORITY.SCHEDULE_STALE;
    case 'team_assignments':
      return PRIORITY.TEAM_ASSIGNMENT;
    case 'members_imported':
      return PRIORITY.MEMBERS_MISSING;
    case 'twitch_linked':
    case 'eventsub_ready':
      return PRIORITY.TWITCH_MISSING;
    case 'live_detection_active':
      return PRIORITY.SETUP_INCOMPLETE;
    default:
      return PRIORITY.SETUP_INCOMPLETE;
  }
}

function mapReadinessBlockers(context: CommandCenterBlockerContext): CommandCenterBlocker[] {
  const readiness = context.readiness;
  if (!readiness) return [];

  const blockers: CommandCenterBlocker[] = [];
  const seenIds = new Set<string>();

  for (const item of readiness.blocking_items ?? []) {
    if (!READINESS_LAUNCH_KEYS.has(item.key)) continue;
    const id = `readiness.${item.key}`;
    if (seenIds.has(id)) continue;
    const mapped = blockerFromReadinessItem(item, context, {
      id,
      category: item.key === 'billing_entitlement' ? 'billing_access' : 'launch',
      priority: readinessPriorityForKey(item.key, context),
      blocksLaunch: true,
      blocksAutomation: true,
    });
    if (mapped) {
      blockers.push(mapped);
      seenIds.add(id);
    }
  }

  for (const item of readiness.warning_items ?? []) {
    if (!READINESS_LAUNCH_KEYS.has(item.key) || !item.is_actionable) continue;
    const id = `readiness.warning.${item.key}`;
    if (seenIds.has(id) || seenIds.has(`readiness.${item.key}`)) continue;
    const mapped = blockerFromReadinessItem(item, context, {
      id,
      category: item.key === 'billing_entitlement' ? 'billing_access' : 'launch',
      priority: readinessPriorityForKey(item.key, context) + 5,
      blocksLaunch: false,
      blocksAutomation: item.key !== 'billing_entitlement',
    });
    if (mapped) {
      blockers.push(mapped);
      seenIds.add(id);
    }
  }

  if (
    !context.isOwner &&
    !blockers.some((blocker) => blocker.id === 'readiness.billing_entitlement')
  ) {
    const billingItem = readiness.items?.find((item) => item.key === 'billing_entitlement');
    if (billingItem?.status === 'not_applicable') {
      blockers.push({
        id: 'billing.owner_managed',
        category: 'billing_access',
        severity: 'warning',
        title: 'Subscription managed by league owner',
        description: COMMISSIONER_ONLY_NOTICE,
        primaryActionLabel: 'Ask a commissioner',
        source: 'league_readiness',
        customerSafeReason: COMMISSIONER_ONLY_NOTICE,
        blocksLaunch: true,
        blocksAutomation: true,
        requiresCommissioner: true,
        isSystemWaiting: false,
        isResolved: false,
        priority: PRIORITY.BILLING_OWNER_MANAGED,
      });
    }
  }

  return blockers;
}

function mapBillingLifecycleBlockers(context: CommandCenterBlockerContext): CommandCenterBlocker[] {
  const billing = context.billingStatus;
  if (!billing?.billing_lifecycle?.mode) return [];

  const mode = (billing.billing_lifecycle.mode || '').toLowerCase();
  const message =
    billing.billing_lifecycle.customer_message ||
    (mode === 'billing_unbound'
      ? 'Your subscription is active, but it is not linked to a league yet.'
      : billing.billing_lifecycle.customer_message);

  if (mode === 'billing_unbound' || billing.billing_lifecycle.recovery_available) {
    if (
      context.readiness?.blocking_items?.some((item) => item.key === 'billing_entitlement') ||
      context.readiness?.warning_items?.some((item) => item.key === 'billing_entitlement')
    ) {
      return [];
    }
    return [
      {
        id: 'billing.lifecycle_unbound',
        category: 'billing_access',
        severity: 'action_required',
        title: 'Link subscription to league',
        description:
          message || 'Your subscription is active, but it is not linked to a league yet.',
        primaryActionLabel: context.isOperator ? 'Open league settings' : 'Ask a commissioner',
        primaryActionHref: context.isOperator
          ? leagueHref(context.leagueId, '/settings')
          : undefined,
        source: 'billing_lifecycle',
        customerSafeReason:
          message || 'Your subscription is active, but it is not linked to a league yet.',
        blocksLaunch: true,
        blocksAutomation: true,
        requiresCommissioner: !context.isOperator,
        isSystemWaiting: false,
        isResolved: false,
        priority: PRIORITY.BILLING_UNBOUND,
      },
    ];
  }

  if (
    mode === 'billing_past_due' ||
    mode === 'billing_canceled' ||
    mode === 'billing_incomplete' ||
    mode === 'billing_unpaid' ||
    mode === 'billing_checkout_required'
  ) {
    if (
      context.readiness?.blocking_items?.some((item) => item.key === 'billing_entitlement')
    ) {
      return [];
    }
    return [
      {
        id: `billing.lifecycle_${mode.replace('billing_', '')}`,
        category: 'billing_access',
        severity: mode === 'billing_checkout_required' ? 'blocking' : 'action_required',
        title:
          mode === 'billing_past_due'
            ? 'Update payment method'
            : mode === 'billing_canceled'
              ? 'Resubscribe to restore access'
              : mode === 'billing_checkout_required'
                ? 'Subscription required'
                : 'Billing needs attention',
        description: message || 'Billing or access is blocking paid league features.',
        primaryActionLabel: context.isOwner
          ? mode === 'billing_canceled' || mode === 'billing_checkout_required'
            ? 'Open pricing'
            : 'Manage billing'
          : 'Ask a commissioner',
        primaryActionHref: context.isOwner
          ? mode === 'billing_canceled' || mode === 'billing_checkout_required'
            ? lscPath('/pricing')
            : lscPath('/billing')
          : undefined,
        source: 'billing_lifecycle',
        customerSafeReason: message || 'Billing or access is blocking paid league features.',
        blocksLaunch: true,
        blocksAutomation: true,
        requiresCommissioner: !context.isOwner,
        isSystemWaiting: false,
        isResolved: false,
        priority: PRIORITY.BILLING_INACTIVE,
      },
    ];
  }

  return [];
}

function mapSyncStatusBlockers(context: CommandCenterBlockerContext): CommandCenterBlocker[] {
  const sync = context.syncStatus;
  if (!sync) return [];

  const importHref = leagueHref(context.leagueId, '/import');
  const scheduleHref = leagueHref(context.leagueId, '/settings?tab=schedule');

  if (sync.rollover_review_required) {
    return [
      {
        id: 'schedule.season_rollover',
        category: 'review',
        severity: 'action_required',
        title: 'New season schedule detected',
        description: `${SCHEDULE_SYNC_ROLLOVER_DETECTED_COPY} ${SCHEDULE_SYNC_ROLLOVER_REVIEW_COPY}`.trim(),
        primaryActionLabel: 'Review schedule import',
        primaryActionHref: importHref,
        source: 'sync_status',
        customerSafeReason: SCHEDULE_SYNC_ROLLOVER_DETECTED_COPY,
        blocksLaunch: true,
        blocksAutomation: true,
        requiresCommissioner: !context.isOperator,
        isSystemWaiting: false,
        isResolved: false,
        priority: PRIORITY.SEASON_ROLLOVER,
      },
    ];
  }

  if (sync.source_state_stale || sync.is_stale) {
    return [
      {
        id: 'schedule.stale',
        category: 'launch',
        severity: 'action_required',
        title: 'Schedule repair needed',
        description: SCHEDULE_REPAIR_DETECTED_COPY,
        primaryActionLabel: context.isOperator ? SCHEDULE_REPAIR_PREVIEW_CTA : 'Ask a commissioner',
        primaryActionHref: context.isOperator
          ? `${scheduleHref}#schedule-repair-section`
          : undefined,
        source: 'sync_status',
        customerSafeReason: SCHEDULE_REPAIR_DETECTED_COPY,
        blocksLaunch: false,
        blocksAutomation: true,
        requiresCommissioner: !context.isOperator,
        isSystemWaiting: false,
        isResolved: false,
        priority: PRIORITY.SCHEDULE_STALE,
      },
    ];
  }

  if (sync.recommended_action === 'team_assignment_review' || sync.recommended_action === 'resolve_team_assignments') {
    return [
      {
        id: 'schedule.team_assignment_review',
        category: 'review',
        severity: 'action_required',
        title: 'Team assignment review required',
        description: SCHEDULE_SYNC_TEAM_ASSIGNMENT_REVIEW_COPY,
        primaryActionLabel: context.isOperator ? 'Review team assignments' : 'Ask a commissioner',
        primaryActionHref: context.isOperator
          ? leagueHref(context.leagueId, '/members')
          : undefined,
        source: 'sync_status',
        customerSafeReason: SCHEDULE_SYNC_TEAM_ASSIGNMENT_REVIEW_COPY,
        blocksLaunch: false,
        blocksAutomation: true,
        requiresCommissioner: !context.isOperator,
        isSystemWaiting: false,
        isResolved: false,
        priority: PRIORITY.TEAM_ASSIGNMENT,
      },
    ];
  }

  const guarded = sync.guarded_auto_sync_apply;

  if (guarded?.mode === 'auto_sync_apply_dry_run' || guarded?.mode === 'auto_sync_apply_preview_ready') {
    return [
      {
        id: 'schedule.guarded_auto_sync_preview',
        category: 'waiting',
        severity: 'optional',
        title: 'Schedule sync is preview only',
        description: guarded.customer_message || guardedAutoSyncApplyCustomerCopy(guarded.mode),
        primaryActionLabel: context.isOperator ? 'Open schedule setup' : undefined,
        primaryActionHref: context.isOperator ? scheduleHref : undefined,
        source: 'sync_status',
        customerSafeReason: guardedAutoSyncApplyCustomerCopy(guarded.mode),
        blocksLaunch: false,
        blocksAutomation: false,
        requiresCommissioner: !context.isOperator,
        isSystemWaiting: true,
        isResolved: false,
        priority: PRIORITY.SCHEDULE_STALE + 5,
      },
    ];
  }

  if (
    guarded?.mode === 'auto_sync_apply_blocked_billing' ||
    guarded?.mode === 'auto_sync_apply_blocked_permission'
  ) {
    return [
      {
        id: 'schedule.guarded_auto_sync_billing',
        category: 'billing_access',
        severity: 'action_required',
        title: 'Billing or permissions blocking schedule automation',
        description: guarded.customer_message || guardedAutoSyncApplyCustomerCopy(guarded.mode),
        primaryActionLabel: context.isOperator ? 'Open schedule setup' : 'Ask a commissioner',
        primaryActionHref: context.isOperator ? scheduleHref : undefined,
        source: 'sync_status',
        customerSafeReason: guardedAutoSyncApplyCustomerCopy(guarded.mode),
        blocksLaunch: Boolean(guarded.blocks_launch),
        blocksAutomation: true,
        requiresCommissioner: !context.isOperator,
        isSystemWaiting: false,
        isResolved: false,
        priority: PRIORITY.BILLING_INACTIVE,
      },
    ];
  }

  return [];
}

function activationBlockedTitle(status: LscAutomationActivationStatus): string {
  switch (status) {
    case 'locked_unpaid':
      return 'Subscription required before activation';
    case 'blocked':
      return 'Complete setup before activation';
    case 'ready_to_activate':
      return 'Automation ready to activate';
    case 'pending_operator_enablement':
    case 'entitlement_bridge_disabled':
      return 'Activation saved — rollout finishing';
    default:
      return 'Automation activation needs attention';
  }
}

function mapActivationBlockers(context: CommandCenterBlockerContext): CommandCenterBlocker[] {
  const activation = context.activation;
  if (!activation) return [];

  const blockers: CommandCenterBlocker[] = [];
  const machineBlockers = activation.blockers ?? [];
  const setupComplete = context.readiness ? isCoreSetupComplete(context.readiness) : false;
  const activationHref = activation.command_center_href || leagueHref(context.leagueId, '/setup');

  const hasScheduleReadinessBlocker = (context.readiness?.blocking_items ?? []).some((item) =>
    ['schedule_source_connected', 'current_week_synced', 'active_games'].includes(item.key)
  );

  if (
    machineBlockers.includes('billing_inactive') &&
    !blockers.some((item) => item.id.startsWith('readiness.billing'))
  ) {
    blockers.push({
      id: 'activation.billing_inactive',
      category: 'billing_access',
      severity: 'blocking',
      title: 'Subscription required',
      description: 'Activate your LSC subscription before automations can be turned on.',
      primaryActionLabel: context.isOwner ? 'Manage billing' : 'Ask a commissioner',
      primaryActionHref: context.isOwner ? lscPath('/billing') : undefined,
      source: 'automation_activation',
      customerSafeReason: 'Subscription must be active before automation activation.',
      blocksLaunch: true,
      blocksAutomation: true,
      requiresCommissioner: !context.isOperator,
      isSystemWaiting: false,
      isResolved: false,
      priority: PRIORITY.BILLING_INACTIVE,
    });
  }

  if (
    machineBlockers.some((key) => ACTIVATION_SCHEDULE_BLOCKERS.has(key)) &&
    !hasScheduleReadinessBlocker
  ) {
    blockers.push({
      id: 'activation.schedule_blocked',
      category: 'launch',
      severity: 'blocking',
      title: 'Schedule setup incomplete',
      description: 'Connect or refresh your league schedule before requesting automation activation.',
      primaryActionLabel: context.isOperator ? 'Open schedule setup' : 'Ask a commissioner',
      primaryActionHref: context.isOperator
        ? leagueHref(context.leagueId, '/import')
        : undefined,
      source: 'automation_activation',
      customerSafeReason: 'Schedule must be connected before automation activation.',
      blocksLaunch: true,
      blocksAutomation: true,
      requiresCommissioner: !context.isOperator,
      isSystemWaiting: false,
      isResolved: false,
      priority: PRIORITY.SCHEDULE_MISSING,
    });
  }

  if (machineBlockers.includes('zero_broadcasters')) {
    blockers.push({
      id: 'activation.zero_broadcasters',
      category: 'launch',
      severity: 'action_required',
      title: 'Add broadcasters with Twitch',
      description: 'Import members and connect Twitch accounts for at least one broadcaster.',
      primaryActionLabel: context.isOperator ? 'Manage members' : 'Ask a commissioner',
      primaryActionHref: context.isOperator ? leagueHref(context.leagueId, '/members') : undefined,
      source: 'automation_activation',
      customerSafeReason: 'At least one broadcaster with Twitch is required.',
      blocksLaunch: false,
      blocksAutomation: true,
      requiresCommissioner: !context.isOperator,
      isSystemWaiting: false,
      isResolved: false,
      priority: PRIORITY.TWITCH_MISSING,
    });
  }

  const inactiveStatuses: LscAutomationActivationStatus[] = [
    'locked_unpaid',
    'blocked',
    'ready_to_activate',
  ];
  if (
    setupComplete &&
    inactiveStatuses.includes(activation.activation_status) &&
    activation.can_activate
  ) {
    blockers.push({
      id: 'activation.ready_to_activate',
      category: 'automation',
      severity: 'action_required',
      title: 'Activate default automation bundle',
      description: 'Your league setup is complete. Turn on the default automation bundle when you are ready.',
      primaryActionLabel: context.isOperator ? 'Open setup wizard' : 'Ask a commissioner',
      primaryActionHref: context.isOperator ? leagueHref(context.leagueId, '/setup') : undefined,
      source: 'automation_activation',
      customerSafeReason: 'Automation has not been activated yet.',
      blocksLaunch: false,
      blocksAutomation: true,
      requiresCommissioner: !context.isOperator,
      isSystemWaiting: false,
      isResolved: false,
      priority: PRIORITY.AUTOMATION_NOT_ACTIVATED,
    });
  } else if (
    setupComplete &&
    inactiveStatuses.includes(activation.activation_status) &&
    !activation.can_activate &&
    blockers.length === 0
  ) {
    blockers.push({
      id: 'activation.runtime_blocked',
      category: 'automation',
      severity: 'blocking',
      title: activationBlockedTitle(activation.activation_status),
      description: 'Finish the remaining setup checks before automation can be activated.',
      primaryActionLabel: context.isOperator ? 'Review activation' : 'Ask a commissioner',
      primaryActionHref: context.isOperator ? activationHref : undefined,
      source: 'automation_activation',
      customerSafeReason: activationBlockedTitle(activation.activation_status),
      blocksLaunch: false,
      blocksAutomation: true,
      requiresCommissioner: !context.isOperator,
      isSystemWaiting: false,
      isResolved: false,
      priority: PRIORITY.AUTOMATION_RUNTIME_BLOCKED,
    });
  }

  return blockers;
}

function mapReviewBlockers(context: CommandCenterBlockerContext): CommandCenterBlocker[] {
  const blockers: CommandCenterBlocker[] = [];
  const counts = context.digest?.counts;
  const needsAttentionHref = leagueHref(context.leagueId, '/needs-attention');
  const pendingReview = Number(counts?.pending_review ?? 0);
  const needsIdentity = Number(counts?.needs_identity_attestation ?? 0);
  const readyToPublish = Number(counts?.ready_to_publish ?? 0);
  const twitchIssues = Number(counts?.member_twitch_issues ?? 0);
  const reviewTotal = pendingReview + needsIdentity + readyToPublish;

  if (reviewTotal > 0) {
    blockers.push({
      id: 'review.needs_attention',
      category: 'review',
      severity: 'action_required',
      title: 'Needs Attention',
      description: `${reviewTotal} archive item${reviewTotal === 1 ? '' : 's'} need commissioner review before they can move forward.`,
      primaryActionLabel: context.isOperator ? 'Open Needs Attention' : 'Ask a commissioner',
      primaryActionHref: context.isOperator ? needsAttentionHref : undefined,
      source: 'commissioner_digest',
      customerSafeReason: 'Archive items need commissioner review.',
      blocksLaunch: false,
      blocksAutomation: false,
      requiresCommissioner: !context.isOperator,
      isSystemWaiting: false,
      isResolved: false,
      priority: PRIORITY.NEEDS_ATTENTION,
    });
  }

  if (twitchIssues > 0 && !blockers.some((item) => item.id.startsWith('readiness.twitch'))) {
    blockers.push({
      id: 'review.twitch_mapping',
      category: 'launch',
      severity: 'action_required',
      title: 'Twitch mapping needs attention',
      description: `${twitchIssues} member${twitchIssues === 1 ? '' : 's'} still need Twitch linked for live detection.`,
      primaryActionLabel: context.isOperator ? 'Manage members' : 'Ask a commissioner',
      primaryActionHref: context.isOperator ? leagueHref(context.leagueId, '/members') : undefined,
      source: 'commissioner_digest',
      customerSafeReason: 'Members need Twitch accounts linked.',
      blocksLaunch: false,
      blocksAutomation: true,
      requiresCommissioner: !context.isOperator,
      isSystemWaiting: false,
      isResolved: false,
      priority: PRIORITY.TWITCH_MISSING + 5,
    });
  }

  return blockers;
}

function mapHandoffBlockers(context: CommandCenterBlockerContext): CommandCenterBlocker[] {
  const sessions = selectPanelHandoffSessions(context.handoffSessions);
  const blockers: CommandCenterBlocker[] = [];

  for (const session of sessions) {
    const classification = classifyVodHandoffSession(session);
    const matchLabel = formatVodHandoffSessionMatchLabel(session);
    const primaryText = getVodHandoffSessionPrimaryText(session);
    if (!primaryText) continue;

    if (classification === 'awaiting_twitch_vod') {
      blockers.push({
        id: `handoff.awaiting.${session.id}`,
        category: 'waiting',
        severity: 'waiting',
        title: matchLabel ? `Waiting for Twitch VOD — ${matchLabel}` : 'Waiting for Twitch VOD',
        description: primaryText,
        source: 'vod_handoff',
        customerSafeReason: 'LSC verified the game live and is waiting for Twitch to expose the archive VOD.',
        blocksLaunch: false,
        blocksAutomation: false,
        requiresCommissioner: false,
        isSystemWaiting: true,
        isResolved: false,
        priority: PRIORITY.VOD_WAITING,
      });
      continue;
    }

    if (classification === 'retry_pending') {
      blockers.push({
        id: `handoff.retry.${session.id}`,
        category: 'waiting',
        severity: 'waiting',
        title: matchLabel ? `Retrying VOD lookup — ${matchLabel}` : 'Retrying VOD lookup',
        description: primaryText,
        source: 'vod_handoff',
        customerSafeReason: 'LSC is retrying the Twitch VOD lookup automatically.',
        blocksLaunch: false,
        blocksAutomation: false,
        requiresCommissioner: false,
        isSystemWaiting: true,
        isResolved: false,
        priority: PRIORITY.VOD_RETRY,
      });
      continue;
    }

    if (classification === 'candidate_ready_for_review') {
      blockers.push({
        id: `handoff.candidate_ready.${session.id}`,
        category: 'review',
        severity: 'action_required',
        title: matchLabel ? `Archive candidate ready — ${matchLabel}` : 'Archive candidate ready for review',
        description: primaryText,
        primaryActionLabel: context.isOperator ? 'Open review' : 'Ask a commissioner',
        primaryActionHref:
          context.isOperator && session.vod_candidate_id
            ? `/leagues/${encodeURIComponent(context.leagueId)}/vods/${encodeURIComponent(
                String(session.vod_candidate_id)
              )}`
            : undefined,
        source: 'vod_handoff',
        customerSafeReason: 'Archive candidate is ready for commissioner review.',
        blocksLaunch: false,
        blocksAutomation: false,
        requiresCommissioner: !context.isOperator,
        isSystemWaiting: false,
        isResolved: false,
        priority: PRIORITY.ARCHIVE_CANDIDATE_READY,
      });
      continue;
    }

    if (classification === 'retry_exhausted') {
      blockers.push({
        id: `handoff.retry_exhausted.${session.id}`,
        category: 'review',
        severity: 'action_required',
        title: matchLabel ? `VOD lookup needs attention — ${matchLabel}` : 'VOD lookup needs attention',
        description: primaryText,
        primaryActionLabel: context.isOperator ? 'Open Needs Attention' : 'Ask a commissioner',
        primaryActionHref: context.isOperator
          ? leagueHref(context.leagueId, '/needs-attention')
          : undefined,
        source: 'vod_handoff',
        customerSafeReason: 'Automatic VOD lookup retries were exhausted.',
        blocksLaunch: false,
        blocksAutomation: true,
        requiresCommissioner: !context.isOperator,
        isSystemWaiting: false,
        isResolved: false,
        priority: PRIORITY.VOD_RETRY_EXHAUSTED,
      });
      continue;
    }

    if (classification === 'handoff_blocked') {
      blockers.push({
        id: `handoff.side_confirmation.${session.id}`,
        category: 'automation',
        severity: 'action_required',
        title: matchLabel ? `Side confirmation needed — ${matchLabel}` : 'Side confirmation needed',
        description: primaryText,
        primaryActionLabel: context.isOperator ? 'Open Needs Attention' : 'Ask a commissioner',
        primaryActionHref: context.isOperator
          ? leagueHref(context.leagueId, '/needs-attention')
          : undefined,
        source: 'vod_handoff',
        customerSafeReason: 'Confirm broadcaster side before archive handoff can continue.',
        blocksLaunch: false,
        blocksAutomation: true,
        requiresCommissioner: !context.isOperator,
        isSystemWaiting: false,
        isResolved: false,
        priority: PRIORITY.SIDE_CONFIRMATION,
      });
    }
  }

  return blockers;
}

function mapAuthBlockers(context: CommandCenterBlockerContext): CommandCenterBlocker[] {
  if (context.authError === 'unauthorized') {
    return [
      {
        id: 'auth.unauthorized',
        category: 'billing_access',
        severity: 'blocking',
        title: 'Sign in required',
        description: 'Sign in to view league readiness and Command Center actions.',
        primaryActionLabel: 'Sign in',
        primaryActionHref: lscPath('/login'),
        source: 'auth',
        customerSafeReason: 'Authentication is required.',
        blocksLaunch: true,
        blocksAutomation: true,
        requiresCommissioner: false,
        isSystemWaiting: false,
        isResolved: false,
        priority: PRIORITY.UNAUTHORIZED,
      },
    ];
  }
  if (context.authError === 'forbidden') {
    return [
      {
        id: 'auth.forbidden',
        category: 'billing_access',
        severity: 'blocking',
        title: 'Permission required',
        description: COMMISSIONER_ONLY_NOTICE,
        primaryActionLabel: 'Ask a commissioner',
        source: 'auth',
        customerSafeReason: COMMISSIONER_ONLY_NOTICE,
        blocksLaunch: true,
        blocksAutomation: true,
        requiresCommissioner: true,
        isSystemWaiting: false,
        isResolved: false,
        priority: PRIORITY.FORBIDDEN,
      },
    ];
  }
  return [];
}

function mapHealthySignals(context: CommandCenterBlockerContext): CommandCenterBlocker[] {
  const readiness = context.readiness;
  if (!readiness) return [];

  const signals: CommandCenterBlocker[] = [];
  if (isCoreSetupComplete(readiness)) {
    signals.push({
      id: 'healthy.setup_complete',
      category: 'healthy',
      severity: 'healthy',
      title: 'League setup complete',
      description: 'Core setup checks are complete.',
      source: 'league_readiness',
      customerSafeReason: 'Setup checks passed.',
      blocksLaunch: false,
      blocksAutomation: false,
      requiresCommissioner: false,
      isSystemWaiting: false,
      isResolved: true,
      priority: PRIORITY.HEALTHY_SETUP,
    });
  }

  const guarded = context.syncStatus?.guarded_auto_sync_apply;
  if (guarded?.mode === 'auto_sync_apply_eligible') {
    const scheduleHref = leagueHref(context.leagueId, '/settings?tab=schedule');
    signals.push({
      id: 'schedule.guarded_auto_sync_eligible',
      category: 'healthy',
      severity: 'optional',
      title: 'Same-season schedule ready to apply',
      description: guarded.customer_message || guardedAutoSyncApplyCustomerCopy(guarded.mode),
      primaryActionLabel: context.isOperator ? 'Open schedule setup' : undefined,
      primaryActionHref: context.isOperator ? scheduleHref : undefined,
      source: 'sync_status',
      customerSafeReason: guardedAutoSyncApplyCustomerCopy(guarded.mode),
      blocksLaunch: false,
      blocksAutomation: false,
      requiresCommissioner: !context.isOperator,
      isSystemWaiting: false,
      isResolved: true,
      priority: PRIORITY.HEALTHY_AUTOMATION,
    });
  }

  if (context.activation?.activation_status === 'active') {
    signals.push({
      id: 'healthy.automation_active',
      category: 'healthy',
      severity: 'healthy',
      title: 'Automation bundle active',
      description: 'Default automation bundle is active for this league.',
      source: 'automation_activation',
      customerSafeReason: 'Automation is active.',
      blocksLaunch: false,
      blocksAutomation: false,
      requiresCommissioner: false,
      isSystemWaiting: false,
      isResolved: true,
      priority: PRIORITY.HEALTHY_AUTOMATION,
    });
  }

  if (context.activation && context.activation.autopublish_enabled === false) {
    signals.push({
      id: 'healthy.autopublish_off',
      category: 'healthy',
      severity: 'optional',
      title: 'Archive auto-publish is off',
      description: 'Archive auto-publish remains off (Stage 2). Commissioner review is still required before publish.',
      source: 'automation_activation',
      customerSafeReason: 'Auto-publish is off by design.',
      blocksLaunch: false,
      blocksAutomation: false,
      requiresCommissioner: false,
      isSystemWaiting: false,
      isResolved: true,
      priority: PRIORITY.HEALTHY_AUTOPUBLISH_OFF,
    });
  }

  return signals;
}

function dedupeBlockers(blockers: CommandCenterBlocker[]): CommandCenterBlocker[] {
  const byId = new Map<string, CommandCenterBlocker>();
  for (const blocker of blockers.sort((left, right) => left.priority - right.priority)) {
    if (!byId.has(blocker.id)) {
      byId.set(blocker.id, blocker);
    }
  }
  return Array.from(byId.values()).sort((left, right) => left.priority - right.priority);
}

export function buildCommandCenterBlockers(context: CommandCenterBlockerContext): CommandCenterBlocker[] {
  const unresolved = dedupeBlockers([
    ...mapAuthBlockers(context),
    ...mapReadinessBlockers(context),
    ...mapBillingLifecycleBlockers(context),
    ...mapSyncStatusBlockers(context),
    ...mapActivationBlockers(context),
    ...mapReviewBlockers(context),
    ...mapHandoffBlockers(context),
  ]).filter((blocker) => !blocker.isResolved);

  if (unresolved.length === 0) {
    return mapHealthySignals(context);
  }

  return unresolved;
}

export function pickTopCommandCenterBlocker(
  blockers: CommandCenterBlocker[]
): CommandCenterBlocker | null {
  const actionable = blockers.filter((blocker) => !blocker.isResolved && blocker.category !== 'healthy');
  if (actionable.length === 0) return null;
  return actionable[0] ?? null;
}

export function groupCommandCenterBlockersByCategory(
  blockers: CommandCenterBlocker[]
): Partial<Record<CommandCenterBlockerCategory, CommandCenterBlocker[]>> {
  const grouped: Partial<Record<CommandCenterBlockerCategory, CommandCenterBlocker[]>> = {};
  for (const blocker of blockers) {
    const list = grouped[blocker.category] ?? [];
    list.push(blocker);
    grouped[blocker.category] = list;
  }
  return grouped;
}

export function commandCenterBlockerToNextAction(
  blocker: CommandCenterBlocker
): {
  priority: number;
  title: string;
  detail: string;
  actionLabel: string;
  actionHref: string;
  severity: 'blocked' | 'warning' | 'info' | 'success';
} {
  const severity =
    blocker.severity === 'blocking'
      ? 'blocked'
      : blocker.severity === 'waiting' || blocker.severity === 'optional'
        ? 'info'
        : blocker.severity === 'healthy'
          ? 'success'
          : 'warning';

  return {
    priority: blocker.priority,
    title: blocker.title,
    detail: blocker.description,
    actionLabel: blocker.primaryActionLabel ?? (blocker.isSystemWaiting ? 'No action needed' : 'Open'),
    actionHref: blocker.primaryActionHref ?? lscPath('/lsc'),
    severity,
  };
}

export function assertCustomerSafeBlockerCopy(text: string): boolean {
  return !INTERNAL_COPY_PATTERN.test(text);
}
